import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { debounceTime, map, Subject, takeUntil } from 'rxjs';
import { OlxLocation } from '../../../../../../../modules/crm/power-olx-v2/interfaces/power-olx-v2.interface';
import { OlxLocationForm } from '../../advert-action.interface';

@Component({
  selector: 'app-olx-location-dropdown',
  templateUrl: './olx-location-dropdown.component.html',
  styleUrl: './olx-location-dropdown.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => OlxLocationDropdownComponent),
    },
  ],
})
export class OlxLocationDropdownComponent implements OnChanges, OnInit, OnDestroy, ControlValueAccessor {
  private readonly destroy$: Subject<void> = new Subject<void>();

  @Input() formControl!: FormControl<OlxLocationForm | null>;
  @Input() locations: OlxLocation[] = [];
  @Input() selectedLocation?: OlxLocation;
  @Input() loading: boolean = false;

  @Output() onSearchDropdown: EventEmitter<string> = new EventEmitter<string>();
  @Output() locationChanged: EventEmitter<OlxLocation> = new EventEmitter<OlxLocation>();

  protected searchControl: FormControl<string | null> = new FormControl<string | null>('');
  protected filteredLocations: OlxLocation[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locations']) {
      this.updateFilteredLocations();
    }
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        map((term) => {
          this.loading = true;

          if (term!.length < 2) {
            this.onSearchDropdown.emit('');
          }

          return term;
        }),
        debounceTime(300),
      )
      .subscribe((term) => {
        this.loading = false;

        const search = term!.toLowerCase() ?? '';

        if (search.length < 2) {
          return;
        }

        this.onSearchDropdown.emit(search);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  writeValue(value: any): void {}

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  compareLocations = (a: OlxLocationForm, b: OlxLocationForm): boolean => {
    return a?.city_id === b?.city_id && a?.district_id === b?.district_id;
  };

  private updateFilteredLocations(): void {
    const selected = this.selectedLocation;
    this.filteredLocations = [
      ...(selected ? [selected] : []),
      ...this.locations.filter((i) => !(i.city_id === selected?.city_id && i.district_id === selected?.district_id)),
    ];
  }

  protected onChange(locationForm: OlxLocationForm): void {
    const found = this.filteredLocations.find(
      (item) => item.city_id === locationForm.city_id && item.district_id === locationForm.district_id,
    );
    if (found) {
      this.selectedLocation = found;
      this.locationChanged.emit(found);
    }
    this.updateFilteredLocations();

    this.onChangeFn(locationForm);
  }

  protected readonly Boolean = Boolean;
}
